/**
 * Talks to the Mivio API on the same address (no CORS, cookies only).
 *  - `api(path)` works inside the business that's open: /v1/b/<business><path>.
 *  - `rootApi(path)` is for things above any business: your login, your businesses, invites.
 * Errors come back as ApiError, with field messages keyed by field when a save was refused.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Field messages from a refused save, keyed by field path. */
    public errors?: Record<string, unknown>,
    public details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export const SIGNED_OUT_EVENT = "mivio:signed-out";

let HANDLE = "";
/** The business this page is in (set when its router starts). */
export function setBusinessHandle(handle: string): void {
  HANDLE = handle;
}
export function businessHandle(): string {
  return HANDLE;
}

type Init = RequestInit & { json?: unknown };

async function call<T>(url: string, init: Init = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(url, { ...init, body, headers, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "offline", "Can't reach Mivio. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const e = ((data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error ?? {});
    const fields = (e.details?.fields as { field: string; message: string }[] | undefined) ?? [];
    const errors: Record<string, string> = {};
    for (const f of fields) if (f.field && !errors[f.field]) errors[f.field] = f.message;
    if (typeof e.details?.field === "string" && e.message) errors[e.details.field as string] = e.message;
    if (res.status === 401 && !url.includes("/v1/auth/")) window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    throw new ApiError(res.status, e.code ?? "request_failed", e.message ?? `The request failed (${res.status}).`, Object.keys(errors).length ? errors : undefined, e.details);
  }
  return data as T;
}

/** Inside the open business. */
export function api<T>(path: string, init: Init = {}): Promise<T> {
  if (!HANDLE) return Promise.reject(new ApiError(0, "no_business", "Open a business first."));
  return call<T>(`/v1/b/${HANDLE}${path}`, init);
}

/** Saves a file the open business makes (a CSV of orders, say) to the person's computer as `name`. */
export async function download(path: string, name: string): Promise<void> {
  if (!HANDLE) throw new ApiError(0, "no_business", "Open a business first.");
  let res: Response;
  try {
    res = await fetch(`/v1/b/${HANDLE}${path}`, { credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "offline", "Can't reach Mivio. Check your connection and try again.");
  }
  if (!res.ok) {
    const e = ((await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? "request_failed", e?.message ?? `The download didn't work (${res.status}).`);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Above any business (your login, your businesses, invites, notifications). */
export function rootApi<T>(path: string, init: Init = {}): Promise<T> {
  return call<T>(`/v1${path}`, init);
}

export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : "Something went wrong. Try again.";
}

/* ---------- shapes ---------- */

export interface Account {
  id: string;
  email: string;
  name: string;
  locale: string;
  timeZone: string;
  currency: string;
  hq: "owner" | "staff" | null;
  emailVerified: boolean;
  createdAt: string;
}

export interface Me {
  account: Account;
  security: { authenticator: boolean; backupCodesLeft: number; passkeys: number; trustedBrowsers: number };
}

export type SecondFactor = "email_code" | "totp" | "backup_code";

export type SignInStep =
  | ({ next: "done" } & Me)
  | { next: "verify_email"; challenge: string }
  | { next: "second_factor"; challenge: string; methods: SecondFactor[] };

export interface BusinessSummary {
  id: string;
  handle: string;
  name: string;
  status: string;
  currency: string;
  timeZone: string;
  locale: string;
  learning: "full" | "limited" | "off";
  kind?: "business" | "hq";
  createdAt: string;
  role?: { key: string; name: string };
}

export interface BusinessOverview {
  business: BusinessSummary;
  me: { role: { id: string; key: string; name: string; permissions: string[] }; permissions: string[]; owner: boolean };
  nav: { label: string; path: string; solution: string; icon: string | null }[];
  /** Keys of the solutions switched on in this business. */
  solutions?: string[];
  /** Its members' site. */
  siteUrl?: string | null;
}

export interface SearchResult { solution: string; title: string; subtitle?: string; path: string }

export interface UploadedFile {
  id: string;
  /** "" for any picture; "route-map" for a route map (offered only where a route map goes). */
  kind?: "" | "route-map";
  name: string;
  type: string;
  bytes: number;
  width: number | null;
  height: number | null;
  visibility: "site" | "staff";
  alt?: string;
  caption?: string;
  url: string | null;
  adminPath: string;
  uploadedBy: string;
  createdAt: string;
}

/** Sends a picture or a PDF as it is (no JSON): the bytes as the body, its name in a header. PDFs stay with the team. */
export async function uploadFile(file: File, visibility: "site" | "staff" = file.type === "application/pdf" ? "staff" : "site", kind: "" | "route-map" = ""): Promise<UploadedFile> {
  let res: Response;
  const qs = new URLSearchParams();
  if (visibility === "staff") qs.set("visibility", "staff");
  if (kind) qs.set("kind", kind);
  try {
    res = await fetch(`/v1/b/${HANDLE}/files${qs.size ? `?${qs}` : ""}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": file.type || "application/octet-stream", "x-file-name": encodeURIComponent(file.name) },
      body: file,
    });
  } catch {
    throw new ApiError(0, "offline", "Can't reach Mivio. Check your connection and try again.");
  }
  const data = (await res.json().catch(() => ({}))) as { file?: UploadedFile; error?: { code?: string; message?: string } };
  if (!res.ok || !data.file) throw new ApiError(res.status, data.error?.code ?? "error", data.error?.message ?? "That didn't upload. Try again.");
  return data.file;
}
